import { mdiChevronDown, mdiChevronRight } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { Badge } from '../ui/badge'
import { Card } from '../ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import { AppIcon } from '../ui/icon'
import type { StepStatus } from './assembly-utils'

export interface StepDef {
    id: string
    title: string
    status: StepStatus
    /** One line shown next to the title while the step is closed. */
    summary?: string
    content: React.ReactNode
}

const STATUS_VARIANT: Record<StepStatus, 'success' | 'outline' | 'warning'> = {
    done: 'success',
    todo: 'outline',
    blocked: 'warning',
}

/**
 * The steps of a phase, one block open at a time, each with a status chip
 * (done / to do / blocked). Pattern "step list" (docs/frontend/patterns.md).
 * `open` is controlled by the page so the step the next action belongs to can
 * be opened for the user.
 */
export function StepList({
    steps,
    open,
    onOpenChange,
    testId,
}: {
    steps: StepDef[]
    open: string | undefined
    onOpenChange: (id: string | undefined) => void
    testId: string
}) {
    const { t } = useTranslation('assemblies')
    return (
        <div className="space-y-2" data-testid={testId}>
            {steps.map((step, i) => {
                const isOpen = open === step.id
                return (
                    <Card key={step.id} data-testid={`${testId}-${step.id}`} data-state={isOpen ? 'open' : 'closed'}>
                        <Collapsible open={isOpen} onOpenChange={(next) => onOpenChange(next ? step.id : undefined)}>
                            <CollapsibleTrigger
                                className="flex w-full items-center gap-3 px-4 py-3 text-left"
                                data-testid={`${testId}-${step.id}-toggle`}
                            >
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
                                    {i + 1}
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block font-medium">{step.title}</span>
                                    {!isOpen && step.summary && (
                                        <span className="block truncate text-xs text-muted-foreground" data-testid={`${testId}-${step.id}-summary`}>
                                            {step.summary}
                                        </span>
                                    )}
                                </span>
                                <Badge variant={STATUS_VARIANT[step.status]} data-testid={`${testId}-${step.id}-status`}>
                                    {t(`steps.status.${step.status}`)}
                                </Badge>
                                <AppIcon path={isOpen ? mdiChevronDown : mdiChevronRight} size="sm" tone="muted" />
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                <div className="space-y-3 border-t px-4 py-4">{step.content}</div>
                            </CollapsibleContent>
                        </Collapsible>
                    </Card>
                )
            })}
        </div>
    )
}

/** The one primary action of a phase, with the sentence that explains it. */
export function NextAction({
    label,
    hint,
    children,
    testId,
}: {
    label: string
    hint?: string
    children?: React.ReactNode
    testId: string
}) {
    return (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/25 bg-primary/5 px-4 py-3" data-testid={testId}>
            <div className="min-w-0">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
                {hint && <div className="text-sm">{hint}</div>}
            </div>
            {children}
        </div>
    )
}
